import axios, { AxiosInstance } from "axios";
import API_CONFIG from "../config/api.config";
import authRepository from "./auth.repository";

export type MemberRole = "ADMIN" | "READ_ONLY";

export interface Member {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  role: MemberRole;
  imageUrl: string | null;
}

export interface Invite {
  email: string;
  role: MemberRole;
  lastSent?: string; // ISO date e.g. "2026-02-01T17:46:52Z"
}

export interface AddMembersPayload {
  members: Array<{
    email: string;
    staffRole: MemberRole;
  }>;
}

export interface InviteMemberError {
  email: string;
  message: string;
}

export interface InviteMembersResponse {
  createdStaff: Member[];
  invited: string[];
  errors: InviteMemberError[];
}

class MembersRepository {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: { "Content-Type": "application/json" },
    });

    this.api.interceptors.request.use((config) => {
      const authHeaders = authRepository.getAuthHeader();
      if (authHeaders) config.headers = { ...config.headers, ...authHeaders };
      return config;
    });

    this.api.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.response?.status === 401) {
          authRepository.clearAuthData();
          window.location.href = "/broker/login";
        }
        return Promise.reject(error);
      },
    );
  }


  // GET /api/brokers/staff/members   
  async getStaffMembers(): Promise<Member[]> {
    const res = await this.api.get(API_CONFIG.MEMBERS.MEMBERS);
    return Array.isArray(res.data) ? res.data : [];
  }

  // GET /api/brokers/staff/invited 
  async getStaffInvited(): Promise<Invite[]> {
    const res = await this.api.get(API_CONFIG.MEMBERS.INVITED);
    return Array.isArray(res.data) ? res.data : [];
  }

  // POST /api/brokers/staff
  async addMembers(payload: AddMembersPayload): Promise<InviteMembersResponse> {
    const res = await this.api.post(API_CONFIG.MEMBERS.BASE, payload);
    const data = res.data ?? {};
    return {
      createdStaff: Array.isArray(data.createdStaff) ? data.createdStaff : [],
      invited: Array.isArray(data.invited) ? data.invited : [],
      errors: Array.isArray(data.errors) ? data.errors : [],
    };
  }


  //DELETE /api/brokers/staff/invited?email=...       
  async deleteInvitedByEmail(email: string): Promise<void> {
    await this.api.delete(API_CONFIG.MEMBERS.DELETE_INVITE, {
      params: { email },
    });
  }

  // POST /api/brokers/staff/invited/resend?email=...
  async resendStaffInvite(email: string): Promise<void> {
    await this.api.post(API_CONFIG.MEMBERS.INVITED_RESEND, null, {
      params: { email },
    });
  }

  // POST /api/brokers/staff/signup-from-invite (multipart: DTO + image)
  async signupFromInvite(
    dto: {
      accessKey: string;
      email: string;
      firstName: string;
      lastName: string;
      country: string;
      phoneNumber: string;
      username: string;
      password: string;
    },
    imageFile: File | null
  ): Promise<unknown> {
    const form = new FormData();
    form.append("accessKey", dto.accessKey ?? "");
    form.append("email", dto.email ?? "");
    form.append("firstName", dto.firstName ?? "");
    form.append("lastName", dto.lastName ?? "");
    form.append("country", dto.country ?? "");
    form.append("phoneNumber", dto.phoneNumber ?? "");
    form.append("username", dto.username ?? "");
    form.append("password", dto.password ?? "");
    if (imageFile) {
      form.append("image", imageFile, imageFile.name);
    }
    const res = await this.api.post(API_CONFIG.MEMBERS.SIGNUP_FROM_INVITE, form, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Authorization": "Bearer eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJjYXJsb3NjaGVzIiwiYXV0aCI6IlJPTEVfQURNSU4sUk9MRV9CUk9LRVJfQURNSU4sUk9MRV9VU0VSIiwiaWF0IjoxNzcxNDM1MzMwLCJleHAiOjE3NzE1MjE3MzAsInBpZCI6NzF9.2REG03ziYwJDXQLn_5rmqFTZ3T5xAwJvlI1mGSkzUEFC2WRqFjofRTAI2oH_yRlOgnmbOlPN4Pldgu9p6eZXYg"
      },
    });
    return res.data;
  }
}

export default new MembersRepository();
